import type { AssessmentDetailPaperQuestion } from "@/lib/mock/assessment-detail-paper"
import { contentIdForTopic } from "@/lib/blueprint/mapping"
import {
  isPacemakerFamilyStem,
  isTelemetryPacemakerStem,
  pacemakerLeoReplace,
  pacemakerLeoSuggestions,
  type AuthoredItem,
} from "@/lib/question-answer-key"

type LeoDraftSeed = Pick<
  AssessmentDetailPaperQuestion,
  "stem" | "title" | "type" | "topic" | "blooms" | "points" | "difficulty"
>

const SECTION_SEEDS: Record<string, LeoDraftSeed[]> = {
  A: [
    {
      stem: "A telemetry strip is regular at 72/min with a P wave before each QRS. Which structure is setting this rate?",
      title: "Sinus pacemaker",
      type: "Multiple Choice",
      topic: "Cardiology",
      blooms: "Remembering",
      points: 5,
      difficulty: "Easy",
    },
    {
      stem: "A 74-year-old with HFpEF is sitting bolt upright and coughing pink frothy sputum. Which assessment is the priority?",
      title: "HFpEF flash edema",
      type: "Multiple Choice",
      topic: "Cardiology",
      blooms: "Applying",
      points: 5,
      difficulty: "Medium",
    },
    {
      stem: "A client with an anterior-wall STEMI is going to PCI. Click the left anterior descending artery on the coronary diagram.",
      title: "LAD artery hotspot",
      type: "Hotspot",
      topic: "Cardiology",
      blooms: "Remembering",
      points: 5,
      difficulty: "Medium",
    },
    {
      stem: "After a pelvic fracture, pulse is 128, BP is 88/54, and the skin is cool and clammy. Which compensatory response explains these findings?",
      title: "Hypovolemia compensation",
      type: "Multiple Choice",
      topic: "Physiology",
      blooms: "Understanding",
      points: 5,
      difficulty: "Medium",
    },
    {
      stem: "Chest pain started 30 minutes ago. The ECG shows ST elevation in II, III, and aVF. Which coronary territory is ischemic?",
      title: "Inferior STEMI territory",
      type: "Multiple Choice",
      topic: "Cardiology",
      blooms: "Applying",
      points: 5,
      difficulty: "Medium",
    },
    {
      stem: "Six hours after an anterior MI, BP is 78/50, crackles reach the apices, and the legs are mottled. Which problem is treated first?",
      title: "Cardiogenic shock",
      type: "Multiple Choice",
      topic: "Cardiology",
      blooms: "Analyzing",
      points: 5,
      difficulty: "Medium",
    },
    {
      stem: "S2 splits on inspiration and becomes single on expiration. What does this finding indicate?",
      title: "Physiologic split S2",
      type: "Multiple Choice",
      topic: "Cardiology",
      blooms: "Understanding",
      points: 5,
      difficulty: "Medium",
    },
    {
      stem: "A harsh systolic murmur is loudest at the right upper sternal border and radiates to the carotids. Which valve lesion produces this sound?",
      title: "Aortic stenosis murmur",
      type: "Multiple Choice",
      topic: "Cardiology",
      blooms: "Remembering",
      points: 5,
      difficulty: "Easy",
    },
    {
      stem: "A 79-year-old has a slow-rising carotid pulse and fainted while climbing stairs. Which valvular lesion is most likely?",
      title: "Delayed carotid upstroke",
      type: "Multiple Choice",
      topic: "Cardiology",
      blooms: "Applying",
      points: 5,
      difficulty: "Medium",
    },
    {
      stem: "Anterior-wall STEMI is produced by occlusion of the _________ artery.",
      title: "Anterior STEMI artery",
      type: "Fill in the blank",
      topic: "Cardiology",
      blooms: "Remembering",
      points: 5,
      difficulty: "Medium",
    },
  ],
  B: [
    {
      stem: "A client takes warfarin 5 mg nightly. Which laboratory value is used to adjust tomorrow's dose?",
      title: "Warfarin monitoring",
      type: "Multiple Choice",
      topic: "Pharmacology",
      blooms: "Remembering",
      points: 5,
      difficulty: "Easy",
    },
    {
      stem: "A client in anaphylaxis has wheezing and a BP of 72/40. Two minutes after IM epinephrine, which finding shows the dose is working?",
      title: "Epinephrine response",
      type: "Multiple Choice",
      topic: "Pharmacology",
      blooms: "Applying",
      points: 5,
      difficulty: "Medium",
    },
    {
      stem: "Lisinopril was started 3 days ago. Today's potassium is 5.6 mEq/L. Which action is indicated?",
      title: "ACE inhibitor potassium",
      type: "Multiple Choice",
      topic: "Pharmacology",
      blooms: "Applying",
      points: 5,
      difficulty: "Medium",
    },
    {
      stem: "Which antibiotic classes weaken the bacterial cell wall? Select all that apply.",
      title: "Cell wall antibiotics",
      type: "Multiple Select",
      topic: "Pharmacology",
      blooms: "Remembering",
      points: 5,
      difficulty: "Easy",
    },
    {
      stem: "A client on digoxin reports yellow halos around lights and a pulse of 46. Which action is the priority?",
      title: "Digoxin toxicity",
      type: "Multiple Choice",
      topic: "Pharmacology",
      blooms: "Applying",
      points: 5,
      difficulty: "Medium",
    },
    {
      stem: "Clarithromycin is added for a client who takes simvastatin. Which finding is the earliest sign of a dangerous interaction?",
      title: "CYP3A4 interaction",
      type: "Multiple Choice",
      topic: "Pharmacology",
      blooms: "Applying",
      points: 5,
      difficulty: "Medium",
    },
    {
      stem: "A client with eGFR 18 mL/min is prescribed vancomycin 1 g IV every 8 hours. Which action is indicated?",
      title: "Renal dose adjustment",
      type: "Multiple Choice",
      topic: "Pharmacology",
      blooms: "Applying",
      points: 5,
      difficulty: "Hard",
    },
    {
      stem: "Furosemide 40 mg IV is ordered for flash pulmonary edema. Where in the nephron does this drug act?",
      title: "Loop diuretic site",
      type: "Multiple Choice",
      topic: "Pharmacology",
      blooms: "Remembering",
      points: 5,
      difficulty: "Easy",
    },
    {
      stem: "A client who started amiodarone 2 weeks ago has a dry cough and new dyspnea at rest. Which action is indicated?",
      title: "Amiodarone lung toxicity",
      type: "Multiple Choice",
      topic: "Pharmacology",
      blooms: "Applying",
      points: 5,
      difficulty: "Medium",
    },
    {
      stem: "Hold the evening warfarin dose when today's INR is above the ordered range.",
      title: "Warfarin hold rule",
      type: "True or False",
      topic: "Pharmacology",
      blooms: "Remembering",
      points: 5,
      difficulty: "Easy",
    },
  ],
  C: [
    {
      stem: "Match each bedside presentation with the most likely diagnosis.",
      title: "Presentation matching",
      type: "Match the following",
      topic: "Pathology",
      blooms: "Understanding",
      points: 5,
      difficulty: "Medium",
    },
    {
      stem: "A 61-year-old has a temperature of 38.9 C, BP 86/48, and a new holosystolic murmur. Which action is the priority?",
      title: "Fever hypotension murmur",
      type: "Multiple Choice",
      topic: "Diagnostic Reasoning",
      blooms: "Analyzing",
      points: 5,
      difficulty: "Medium",
    },
    {
      stem: "A client has a temperature of 38.1 C, heart rate 102, respiratory rate 22, and a systolic BP of 78 after a fluid bolus. Which finding is treated first?",
      title: "Escalate first finding",
      type: "Multiple Choice",
      topic: "Emergency Recognition",
      blooms: "Evaluating",
      points: 5,
      difficulty: "Medium",
    },
    {
      stem: "A 68-year-old with HFpEF is deciding whether to start an ACE inhibitor. Write the counseling you would give about benefit, risk, and when to call.",
      title: "ACE inhibitor counseling",
      type: "Essay",
      topic: "Patient Communication",
      blooms: "Creating",
      points: 10,
      difficulty: "Medium",
    },
    {
      stem: "Chest pain is now 4/10 after nitroglycerin, troponin is pending, and the ECG is unchanged. What is the next diagnostic step?",
      title: "Next diagnostic step",
      type: "Multiple Choice",
      topic: "Diagnostic Reasoning",
      blooms: "Analyzing",
      points: 5,
      difficulty: "Medium",
    },
    {
      stem: "A 54-year-old arrives from triage with fever, hypotension, and a new murmur. Rank the three diagnoses you would work first, and why.",
      title: "Differential ranking",
      type: "Essay",
      topic: "Diagnostic Reasoning",
      blooms: "Evaluating",
      points: 10,
      difficulty: "Medium",
    },
    {
      stem: "A client with community-acquired pneumonia is going home on oral antibiotics. What return precautions do you give?",
      title: "Discharge safety net",
      type: "Short Answer",
      topic: "Patient Communication",
      blooms: "Applying",
      points: 5,
      difficulty: "Medium",
    },
    {
      stem: "Over 20 minutes BP falls from 118/72 to 86/50 and the client becomes newly confused. Which action is indicated?",
      title: "Rapid response trigger",
      type: "Multiple Choice",
      topic: "Emergency Recognition",
      blooms: "Evaluating",
      points: 5,
      difficulty: "Medium",
    },
    {
      stem: "Write a one-sentence problem representation for a 72-year-old with fever, hypotension, and a new murmur.",
      title: "Problem representation",
      type: "Short Answer",
      topic: "Diagnostic Reasoning",
      blooms: "Creating",
      points: 5,
      difficulty: "Medium",
    },
  ],
}

/** Pause between each item Leo writes onto the paper. */
export const LEO_BLUEPRINT_ITEM_MS = 5600
/** Pause while Leo rewrites or replaces an item already on the paper. */
export const LEO_BLUEPRINT_ALTER_MS = 9600
/** Pause while the section agent checks order and alignment. */
export const LEO_BLUEPRINT_VERIFY_MS = 7200
/** Keep the agent header after review so the Leo star can settle into a check. */
export const LEO_SECTION_DONE_GESTURE_MS = 1800

/** Shortest and longest an item may take, so no row looks instant or stalled. */
export const LEO_ITEM_MS_RANGE = [3600, 7800] as const
/** Review is slower than a single item, and still varies by section. */
export const LEO_VERIFY_MS_RANGE = [5800, 9200] as const

function seededFraction(seed: string): number {
  let hash = 0
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) >>> 0
  }
  return (hash % 1000) / 1000
}

function seededMs(seed: string, range: readonly [number, number]): number {
  const [min, max] = range
  return Math.round(min + seededFraction(seed) * (max - min))
}

/**
 * Real bank lookups do not all take the same time, and a paper where every row
 * ticks on the same beat reads as a progress bar rather than work. Seeded on
 * section and slot so the same paper replays the same way.
 */
export function leoItemDurationMs(sectionId: string, index: number): number {
  return seededMs(`item:${sectionId}:${index}`, LEO_ITEM_MS_RANGE)
}

/** Review time per section, for the same reason. */
export function leoVerifyDurationMs(sectionId: string): number {
  return seededMs(`verify:${sectionId}`, LEO_VERIFY_MS_RANGE)
}

export type LeoSectionAgentMode = "idle" | "working" | "verify" | "done"

export type LeoSectionAgentJob = "fill" | "enhance"

export function leoSectionAgentCopy(
  mode: Exclude<LeoSectionAgentMode, "idle">,
  job: LeoSectionAgentJob = "fill",
): string {
  if (mode === "verify") return "Leo is checking order and alignment"
  if (mode === "done") return "This section is aligned"
  if (job === "enhance") return "Leo is rewriting this section"
  return "Leo is drafting this section"
}

export function leoDraftActLine(input: {
  number: number
  sectionTitle: string
  blooms: string
}): string {
  return `Drafting Q${input.number} for ${input.sectionTitle}, ${input.blooms}`
}

export function isLeoThinkingQuestion(row: {
  leoThinking?: boolean
}): boolean {
  return row.leoThinking === true
}

/** Draft fill hides the stem. Enhance/replace keep the stem and work in the sheet. */
export function isLeoBusyQuestion(row: {
  leoThinking?: boolean
  leoAct?: string
}): boolean {
  return isLeoThinkingQuestion(row) || Boolean(row.leoAct)
}

/** Draft placeholder with no stem yet. Rewrite keeps the slot on the checklist. */
export function isLeoDraftPlaceholder(row: {
  leoThinking?: boolean
  stem?: string
}): boolean {
  return isLeoThinkingQuestion(row) && !row.stem
}

/** New paper items always come from the bank. Leo does not write stems. */
export function seedFromQuestionBank(_seed?: {
  blooms?: string
  difficulty?: string
}): boolean {
  return true
}

export function leoDraftTrustCopy(input: {
  number: number
  sectionTitle: string
  blooms: string
  type: string
  points: number
  mode: "draft" | "rewrite" | "replace"
  fromBank?: boolean
  diversify?: boolean
}): {
  searching: string
  found: string
  matching: string
  writing: string
  constraints: string
  source: "bank" | "created"
} {
  const article = /^[aeiou]/i.test(input.blooms) ? "an" : "a"
  const constraints = [input.blooms, input.type, `${input.points} marks`]
    .filter(Boolean)
    .join(" · ")
  if (input.mode === "rewrite") {
    if (input.diversify) {
      return {
        searching: `Adjusting the wording of Q${input.number}`,
        found: `Steering Q${input.number} away from similar items`,
        matching: `Adjusting the wording of Q${input.number}`,
        writing: `Making a small wording change to Q${input.number}`,
        constraints,
        source: "bank",
      }
    }
    return {
      searching: `Keeping Q${input.number} in this slot`,
      found: `Rewriting the stem for ${input.sectionTitle}`,
      matching: `Keeping Q${input.number} in this slot`,
      writing: `Making a small wording change to Q${input.number}`,
      constraints,
      source: "bank",
    }
  }
  if (input.mode === "replace") {
    return {
      searching: `Searching the question bank for a better Q${input.number}`,
      found: `Finding a better ${input.sectionTitle} match for Q${input.number}`,
      matching: `Finding a better ${input.sectionTitle} match for Q${input.number}`,
      writing: `Replacing Q${input.number} with ${article} ${input.blooms} ${input.type}`,
      constraints,
      source: input.fromBank ? "bank" : "created",
    }
  }
  const searching = `Searching the question bank for Q${input.number}`
  return {
    searching,
    found: "Found a match in the question bank",
    matching: searching,
    writing: `Placing the bank item in Q${input.number}`,
    constraints,
    source: "bank",
  }
}

export function sectionBlueprintTopics(sectionId: string): string[] {
  const seeds = SECTION_SEEDS[sectionId] ?? []
  return Array.from(new Set(seeds.map((seed) => seed.topic)))
}

export function joinEnglishList(items: readonly string[]): string {
  const list = items.filter(Boolean)
  if (list.length === 0) return ""
  if (list.length === 1) return list[0]!
  if (list.length === 2) return `${list[0]} and ${list[1]}`
  return `${list.slice(0, -1).join(", ")}, and ${list[list.length - 1]}`
}

export function uncoveredSectionBlueprintTopics(
  sectionId: string,
  topicsOnPaper: readonly string[],
): string[] {
  const covered = new Set(
    topicsOnPaper.map((topic) => topic.trim()).filter(Boolean),
  )
  return sectionBlueprintTopics(sectionId).filter((topic) => !covered.has(topic))
}

export function questionMissesSectionBlueprint(
  sectionId: string,
  topic: string,
): boolean {
  const topics = sectionBlueprintTopics(sectionId)
  if (topics.length === 0) return false
  return !topics.includes(topic)
}

export type PaperBlueprintSubject = {
  topic?: string
  blooms?: string
  type?: string
  difficulty?: string
}

export type PaperBlueprintCheckKey = "topic" | "blooms" | "type" | "difficulty"

export type PaperBlueprintCheck = {
  key: PaperBlueprintCheckKey
  label: string
  value: string
  aligned: boolean
  plan: string[]
}

export type PaperQuestionBlueprintExplain = {
  aligned: boolean
  title: string
  summary: string
  checks: PaperBlueprintCheck[]
}

function valueOnPlan(
  key: PaperBlueprintCheckKey,
  value: string,
  plan: readonly string[],
): boolean {
  if (!value) return false
  if (plan.includes(value)) return true
  if (key !== "topic") return false
  const categoryId = contentIdForTopic(value)
  if (!categoryId) return false
  return plan.some((item) => contentIdForTopic(item) === categoryId)
}

function uniquePlanValues(values: readonly (string | undefined)[]): string[] {
  return Array.from(
    new Set(values.map((value) => (value ?? "").trim()).filter(Boolean)),
  )
}

export function sectionBlueprintPlan(sectionId: string): {
  topics: string[]
  blooms: string[]
  types: string[]
  difficulties: string[]
} {
  const seeds = SECTION_SEEDS[sectionId] ?? []
  return {
    topics: uniquePlanValues(seeds.map((seed) => seed.topic)),
    blooms: uniquePlanValues(seeds.map((seed) => seed.blooms)),
    types: uniquePlanValues(seeds.map((seed) => seed.type)),
    difficulties: uniquePlanValues(seeds.map((seed) => seed.difficulty)),
  }
}

function blueprintCheck(input: {
  key: PaperBlueprintCheckKey
  label: string
  value: string
  plan: readonly string[]
}): PaperBlueprintCheck | null {
  if (input.plan.length === 0) return null
  const value = input.value.trim()
  return {
    key: input.key,
    label: input.label,
    value: value || "None",
    aligned: valueOnPlan(input.key, value, input.plan),
    plan: [...input.plan],
  }
}

function blueprintExplainSummary(
  aligned: boolean,
  misses: readonly PaperBlueprintCheck[],
): string {
  if (aligned) return "This item is aligned."
  const labels = misses.map((check) =>
    check.label === "Bloom's" ? "Bloom's" : check.label.toLowerCase(),
  )
  return `Not aligned on ${joinEnglishList(labels)}.`
}

export function paperQuestionBlueprintExplain(
  sectionId: string,
  subject: PaperBlueprintSubject | string,
): PaperQuestionBlueprintExplain {
  const row =
    typeof subject === "string" ? { topic: subject } : subject
  const plan = sectionBlueprintPlan(sectionId)
  const hasPlan =
    plan.topics.length +
      plan.blooms.length +
      plan.types.length +
      plan.difficulties.length >
    0
  if (!hasPlan) {
    return {
      aligned: true,
      title: "Aligned",
      summary: "This section has no blueprint targets.",
      checks: [],
    }
  }

  const checks = [
    blueprintCheck({
      key: "topic",
      label: "Category",
      value: row.topic ?? "",
      plan: plan.topics,
    }),
    blueprintCheck({
      key: "blooms",
      label: "Bloom's",
      value: row.blooms ?? "",
      plan: plan.blooms,
    }),
    blueprintCheck({
      key: "type",
      label: "Type",
      value: row.type ?? "",
      plan: plan.types,
    }),
    blueprintCheck({
      key: "difficulty",
      label: "Difficulty",
      value: row.difficulty ?? "",
      plan: plan.difficulties,
    }),
  ].filter((check): check is PaperBlueprintCheck => check !== null)
  const misses = checks.filter((check) => !check.aligned)
  const aligned = misses.length === 0
  return {
    aligned,
    title: aligned ? "Aligned" : "Not aligned",
    summary: blueprintExplainSummary(aligned, misses),
    checks: aligned ? checks : [...misses, ...checks.filter((check) => check.aligned)],
  }
}

export function questionMissesSectionPlan(
  sectionId: string,
  subject: PaperBlueprintSubject,
): boolean {
  return !paperQuestionBlueprintExplain(sectionId, subject).aligned
}

export function paperQuestionBlueprintReasons(
  sectionId: string,
  topic: string,
): string[] {
  if (!questionMissesSectionBlueprint(sectionId, topic)) return []
  const label = topic.trim()
  return [
    label
      ? `${label} is not on this section blueprint`
      : "Category is not on this section blueprint",
  ]
}

/** First Leo draft item: 8x exposure so the paper has one extra Overused issue. */
export const LEO_DRAFT_OVERUSED_COUNT = 8

function toQuestion(
  seed: LeoDraftSeed,
  input: { id: string; number: number; sectionId: string },
): AssessmentDetailPaperQuestion {
  const needsReview = input.number % 5 === 3
  const overused = input.number === 1
  return {
    id: input.id,
    number: input.number,
    stem: seed.stem,
    title: seed.title,
    type: seed.type,
    topic: seed.topic,
    difficulty: seed.difficulty,
    difficultyPercentage:
      seed.difficulty === "Easy" ? 80 : seed.difficulty === "Hard" ? 35 : 60,
    pointBiserial: needsReview ? 0.16 : 0.42,
    discriminationIndex: needsReview ? 0.18 : 0.44,
    blooms: seed.blooms,
    points: seed.points,
    usedBefore: true,
    usedCount: overused ? LEO_DRAFT_OVERUSED_COUNT : 2,
    usedInSameCourse: false,
    sectionId: input.sectionId,
    status: "Ready",
  }
}

/** Draft items Leo inserts to close a blueprint section. */
export function leoInstructionBankOnly(instruction: string): boolean {
  return /bank only|from the bank only|pick replacements from the bank|do not write new|your bank/i.test(
    instruction,
  )
}

export function leoQuestionsForSectionFill(input: {
  sectionId: string
  existingStems: readonly string[]
  count: number
  startNumber: number
  bankOnly?: boolean
}): AssessmentDetailPaperQuestion[] {
  const seeds = SECTION_SEEDS[input.sectionId] ?? SECTION_SEEDS.C
  const taken = new Set(input.existingStems.map((stem) => stem.trim().toLowerCase()))
  const pool = seeds.filter((seed) => seedFromQuestionBank(seed))
  const picked: AssessmentDetailPaperQuestion[] = []
  let number = input.startNumber
  for (const seed of pool) {
    if (picked.length >= input.count) break
    if (taken.has(seed.stem.trim().toLowerCase())) continue
    picked.push(
      toQuestion(seed, {
        id: `leo-${input.sectionId}-${input.startNumber + picked.length}`,
        number,
        sectionId: input.sectionId,
      }),
    )
    number += 1
  }
  return picked
}

const DISTINCT_SEEDS: LeoDraftSeed[] = [
  {
    stem: "A 22-year-old nursing student faints after standing for morning report. Which finding most likely explains the syncope?",
    title: "Orthostatic syncope",
    type: "Multiple Choice",
    topic: "Physiology",
    blooms: "Analyzing",
    points: 5,
    difficulty: "Hard",
  },
  {
    stem: "Which teaching point is the priority before a first-year student administers subcutaneous insulin?",
    title: "Insulin teaching",
    type: "Multiple Choice",
    topic: "Pharmacology",
    blooms: "Applying",
    points: 5,
    difficulty: "Hard",
  },
  {
    stem: "A postoperative patient on morphine has a respiratory rate of 6. What is the priority action?",
    title: "Opioid hypoventilation",
    type: "Multiple Choice",
    topic: "Pharmacology",
    blooms: "Applying",
    points: 5,
    difficulty: "Hard",
  },
  {
    stem: "Which laboratory trend most strongly suggests acute kidney injury after a contrast study?",
    title: "Contrast AKI",
    type: "Multiple Choice",
    topic: "Renal",
    blooms: "Analyzing",
    points: 5,
    difficulty: "Hard",
  },
  {
    stem: "A patient reports new facial droop and slurred speech that started 20 minutes ago. What is the first action?",
    title: "Stroke first action",
    type: "Multiple Choice",
    topic: "Neurology",
    blooms: "Applying",
    points: 5,
    difficulty: "Hard",
  },
  {
    stem: "Which isolation precaution is required for a patient with suspected Clostridioides difficile?",
    title: "C. difficile isolation",
    type: "Multiple Choice",
    topic: "Infection Control",
    blooms: "Remembering",
    points: 5,
    difficulty: "Hard",
  },
]

const STEM_TWEAKS: ReadonlyArray<(stem: string) => string | null> = [
  (stem) => (/^Which /.test(stem) ? stem.replace(/^Which /, "What ") : null),
  (stem) => (/^What /.test(stem) ? stem.replace(/^What /, "Which ") : null),
  (stem) =>
    !/\bmost likely\b/i.test(stem) && /\?$/.test(stem)
      ? stem.replace(/\?$/, " most likely?")
      : null,
  (stem) =>
    /^A patient /.test(stem)
      ? stem.replace(/^A patient /, "A 58-year-old patient ")
      : null,
  (stem) => (/^Click on /.test(stem) ? stem.replace(/^Click on /, "Identify ") : null),
  (stem) =>
    /takes place in:\s*$/i.test(stem)
      ? stem.replace(/takes place in:\s*$/i, "occurs in which location?")
      : null,
  (stem) =>
    !/\bin this case\b/i.test(stem) && /\?$/.test(stem)
      ? stem.replace(/\?$/, " in this case?")
      : null,
]

/** Small wording change that keeps category, topic, and type. */
export function leoMinorStemVariation(stem: string, salt = 0): string {
  const trimmed = stem.trim()
  if (!trimmed) return trimmed
  const start = Math.abs(salt) % STEM_TWEAKS.length
  for (let index = 0; index < STEM_TWEAKS.length; index += 1) {
    const next = STEM_TWEAKS[(start + index) % STEM_TWEAKS.length]!(trimmed)
    if (next && next !== trimmed) return next
  }
  return trimmed.endsWith("?")
    ? trimmed.replace(/\?$/, " for this presentation?")
    : `${trimmed}?`
}

function unusedSeeds(
  pool: readonly LeoDraftSeed[],
  current: string,
  taken: Set<string>,
): LeoDraftSeed[] {
  return pool.filter((seed) => {
    const key = seed.stem.trim().toLowerCase()
    return key !== current && !taken.has(key)
  })
}

function pickAlignSeed(input: {
  sectionId: string
  existingStems: readonly string[]
  mode: "rewrite" | "replace"
  currentStem?: string
  diversify?: boolean
  bankOnly?: boolean
}): LeoDraftSeed | null {
  const seeds = SECTION_SEEDS[input.sectionId] ?? SECTION_SEEDS.C
  const current = (input.currentStem ?? "").trim().toLowerCase()
  const taken = new Set(
    input.existingStems.map((stem) => stem.trim().toLowerCase()),
  )
  if (input.bankOnly) {
    const unused = unusedSeeds(seeds, current, taken)
    const fromBank = unused.filter((seed) => seedFromQuestionBank(seed))
    return (
      fromBank[0] ??
      seeds.find(
        (seed) =>
          seedFromQuestionBank(seed) &&
          seed.stem.trim().toLowerCase() !== current,
      ) ??
      null
    )
  }
  if (input.diversify) {
    const other = Object.entries(SECTION_SEEDS)
      .filter(([id]) => id !== input.sectionId)
      .flatMap(([, list]) => list)
    const pool = unusedSeeds([...DISTINCT_SEEDS, ...other], current, taken)
    return (
      pool.find((seed) => !seedFromQuestionBank(seed)) ??
      pool[0] ??
      DISTINCT_SEEDS[0] ??
      null
    )
  }
  const unused = unusedSeeds(seeds, current, taken)
  if (input.mode === "replace") {
    const fromBank = unused.filter((seed) => seedFromQuestionBank(seed))
    return (
      fromBank[1] ??
      fromBank[0] ??
      unused[0] ??
      seeds.find(
        (seed) =>
          seedFromQuestionBank(seed) &&
          seed.stem.trim().toLowerCase() !== current,
      ) ??
      seeds.find((seed) => seed.stem.trim().toLowerCase() !== current) ??
      seeds[0] ??
      null
    )
  }
  return (
    unused.find((seed) => !seedFromQuestionBank(seed)) ??
    unused[0] ??
    seeds.find((seed) => seed.stem.trim().toLowerCase() !== current) ??
    seeds[0] ??
    null
  )
}

/** Enhance and replace must clear weak PBI, heavy reuse, and same-course use. */
export function healLeoAlignedQuestion(
  row: AssessmentDetailPaperQuestion,
): AssessmentDetailPaperQuestion {
  const fromBank = row.usedBefore === true
  return {
    ...row,
    pointBiserial: 0.46,
    discriminationIndex: 0.48,
    difficultyPercentage:
      row.difficulty === "Easy" ? 80 : row.difficulty === "Hard" ? 38 : 58,
    usedBefore: fromBank,
    usedCount: fromBank ? 1 : 0,
    usedInSameCourse: false,
    status: "Ready",
  }
}

function paperFromAuthoredItem(
  item: AuthoredItem,
  input: {
    keepId: string
    number: number
    sectionId: string
    currentTopic?: string
    currentPoints?: number
  },
): AssessmentDetailPaperQuestion {
  const row = toQuestion(
    {
      stem: item.stem,
      title: item.title,
      type: "Multiple Choice",
      topic: input.currentTopic || "Cardiology",
      blooms: item.blooms,
      points: input.currentPoints ?? 5,
      difficulty: item.difficulty,
    },
    {
      id: input.keepId,
      number: input.number,
      sectionId: input.sectionId,
    },
  )
  return healLeoAlignedQuestion({
    ...row,
    usedBefore: true,
  })
}

function paperFromPacemakerAlign(input: {
  existingStems: readonly string[]
  keepId: string
  number: number
  sectionId: string
  mode: "rewrite" | "replace"
  currentStem?: string
  currentTopic?: string
  currentPoints?: number
  diversify?: boolean
}): AssessmentDetailPaperQuestion | null {
  const current = input.currentStem?.trim() ?? ""
  if (!current) return null
  if (!isPacemakerFamilyStem(current)) return null

  const taken = [current, ...input.existingStems]
  if (input.diversify) {
    const next = pacemakerLeoSuggestions(taken)[0]
    return next ? paperFromAuthoredItem(next, input) : null
  }

  if (isTelemetryPacemakerStem(current)) {
    const replace = pacemakerLeoReplace()
    const alreadyOnPaper = taken.some(
      (stem) => stem.trim().toLowerCase() === replace.stem.trim().toLowerCase(),
    )
    if (!alreadyOnPaper) return paperFromAuthoredItem(replace, input)
  }

  return null
}

/** Next unused blueprint draft for a section, for rewrite or replace. */
export function leoAlignExistingQuestion(input: {
  sectionId: string
  existingStems: readonly string[]
  keepId: string
  number: number
  mode: "rewrite" | "replace"
  currentStem?: string
  currentTitle?: string
  currentTopic?: string
  currentType?: string
  currentBlooms?: string
  currentDifficulty?: string
  currentPoints?: number
  diversify?: boolean
  bankOnly?: boolean
}): AssessmentDetailPaperQuestion | null {
  const pacemaker = paperFromPacemakerAlign(input)
  if (pacemaker) return pacemaker
  if (input.mode === "rewrite" && !input.diversify && input.currentStem?.trim()) {
    const current = input.currentStem.trim()
    const match = (SECTION_SEEDS[input.sectionId] ?? []).find(
      (seed) => seed.stem.trim().toLowerCase() === current.toLowerCase(),
    )
    const seed = match ?? {
      stem: current,
      title: input.currentTitle || "Revised item",
      type: input.currentType || "Multiple Choice",
      topic: input.currentTopic || (SECTION_SEEDS[input.sectionId]?.[0]?.topic ?? ""),
      blooms: input.currentBlooms || "Applying",
      points: input.currentPoints ?? 5,
      difficulty: (input.currentDifficulty as LeoDraftSeed["difficulty"]) || "Medium",
    }
    const row = toQuestion(
      { ...seed, stem: leoMinorStemVariation(current, input.number) },
      {
        id: input.keepId,
        number: input.number,
        sectionId: input.sectionId,
      },
    )
    return healLeoAlignedQuestion({
      ...row,
      topic: seed.topic,
      type: seed.type,
      blooms: seed.blooms,
      usedBefore: true,
    })
  }
  const seed = pickAlignSeed(input)
  if (!seed) return null
  if (input.bankOnly && !seedFromQuestionBank(seed)) return null
  const row = toQuestion(seed, {
    id: input.keepId,
    number: input.number,
    sectionId: input.sectionId,
  })
  return healLeoAlignedQuestion({
    ...row,
    usedBefore: true,
  })
}

/** Rewrite or replace every settled item in a section, each on a distinct stem. */
export function leoAlignSectionQuestions(input: {
  sectionId: string
  questions: readonly {
    id: string
    number: number
    stem: string
    title?: string
    topic?: string
    type?: string
    blooms?: string
    difficulty?: string
    points?: number
  }[]
  existingStems: readonly string[]
  mode: "rewrite" | "replace"
  diversify?: boolean
  bankOnly?: boolean
}): { id: string; aligned: AssessmentDetailPaperQuestion }[] {
  const taken = [...input.existingStems]
  const out: { id: string; aligned: AssessmentDetailPaperQuestion }[] = []
  for (const question of input.questions) {
    const aligned = leoAlignExistingQuestion({
      sectionId: input.sectionId,
      existingStems: taken,
      keepId: question.id,
      number: question.number,
      mode: input.mode,
      currentStem: question.stem,
      currentTitle: question.title,
      currentTopic: question.topic,
      currentType: question.type,
      currentBlooms: question.blooms,
      currentDifficulty: question.difficulty,
      currentPoints: question.points,
      diversify: input.diversify,
      bankOnly: input.bankOnly,
    })
    if (!aligned) continue
    taken.push(aligned.stem)
    out.push({ id: question.id, aligned })
  }
  return out
}

export type LeoAssistIntent = "rewrite" | "replace" | "diversify"

export type LeoEnhancePlan = {
  mode: "rewrite" | "replace"
  scope: "issues" | "all"
  bankOnly: boolean
  diversify: boolean
}

export function leoAssistIntentFromInstruction(
  instruction: string,
): LeoAssistIntent {
  if (/replace|better match|question bank/i.test(instruction)) return "replace"
  if (/similar|different|distinct|unique|duplicate|reuse|same as/i.test(instruction)) {
    return "diversify"
  }
  return "rewrite"
}

export function leoEnhancePlanFromInstruction(
  instruction: string,
): LeoEnhancePlan {
  const text = instruction.trim()
  const bankOnly = leoInstructionBankOnly(text)
  const reworkAll = /rework|overall|existing items/i.test(text)
  const issuesOnly =
    !reworkAll &&
    (!text ||
      /fix what is needed|only fix|items with issues|need review|flagged|not aligned/i.test(
        text,
      ))
  if (bankOnly) {
    return {
      mode: "replace",
      scope: issuesOnly ? "issues" : "all",
      bankOnly: true,
      diversify: false,
    }
  }
  const intent = leoAssistIntentFromInstruction(text)
  if (intent === "replace") {
    return { mode: "replace", scope: issuesOnly ? "issues" : "all", bankOnly: true, diversify: false }
  }
  if (intent === "diversify") {
    return { mode: "rewrite", scope: "all", bankOnly: false, diversify: true }
  }
  return {
    mode: "rewrite",
    scope: issuesOnly ? "issues" : "all",
    bankOnly: false,
    diversify: false,
  }
}

export function leoInstructionFixesExisting(instruction: string): boolean {
  return /only fix|need review|flagged|not aligned|fix items with issues|fix items that need|fix what is needed/i.test(
    instruction,
  )
}

export function questionHasEnhanceIssue(
  sectionId: string,
  row: PaperBlueprintSubject & {
    status?: string
    usedCount?: number
    usedInSameCourse?: boolean
    pointBiserial?: number
  },
  needsReview: boolean,
): boolean {
  return (
    row.status === "Draft" ||
    needsReview ||
    questionMissesSectionPlan(sectionId, row) ||
    row.usedInSameCourse === true ||
    (typeof row.usedCount === "number" && row.usedCount >= 4) ||
    (typeof row.pointBiserial === "number" &&
      row.pointBiserial > 0 &&
      row.pointBiserial < 0.25)
  )
}

/** Overflow / typed instructions that should replace the slot, not rewrite it. */
export function leoAssistModeFromInstruction(
  instruction: string,
): "rewrite" | "replace" {
  return leoAssistIntentFromInstruction(instruction) === "replace"
    ? "replace"
    : "rewrite"
}

export function leoFillCountForState(
  state: "empty" | "few" | "most",
  currentCount: number,
  sectionTarget: number,
): number {
  if (state === "most") return 0
  const target = Math.max(1, sectionTarget)
  if (state === "empty") return Math.min(7, target)
  return Math.max(1, Math.min(4, Math.max(0, target - currentCount)))
}
